import {
  directorDocColumns,
  directorPaidColumns,
  directorWIPColumns,
  hobDocColumns,
  hobPaidColumns,
  hobWIPColumns,
  hobWIPHeaderColumns,
  osmDocColumns,
  osmPaidColumns,
  osmWIPColumns,
  osmWIPHeaderColumns,
  predictedDocColumns,
  predictedPayColumns,
  teamDocColumns,
  teamPaidColumns,
  teamWIPColumns,
  teamWIPHeaderColumns,
  wipHeaderColumns,
} from "@/constants/pipeline";
import { sortPipelineData } from "@/helpers/sorting";
import { formatDate } from "@/lib/utils";
import {
  DashboardPaidModel,
  DashboardPipelineModel,
  DashboardWIPWithTotals,
} from "@/models/pipeline";
import { useMemo, useState } from "react";
import { ComparisonBadge } from "./ComparisonBadge";
import { DashboardRow } from "./DashboardRow";
import { StaticPipelineCard } from "./StaticPipelineCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

type EnrichedPaidModel = DashboardPaidModel & {
  gap: number | null;
  pctFromOriginalGap: number | null;
  pctPaidOverall: number | null;
};

export type DashboardViewVariant = "director" | "hob" | "osm" | "team";

type DashboardViewProps = {
  data: DashboardPipelineModel;
  variant?: DashboardViewVariant;
  onNameClick?: (teamName: string) => void;
  selectedDate: Date;
};

export const DashboardView = ({ data, variant = "director", onNameClick }: DashboardViewProps) => {
  type PredictionsTab = "docs" | "paid";

  const formatPredictedDate = (date?: string) => {
    return date ? formatDate(date) : "-";
  };

  const formatDateOnly = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getDateOnlyFromApiDate = (value: string | undefined): string | null => {
    if (!value) return null;
    const directMatch = value.match(/^\d{4}-\d{2}-\d{2}/);
    if (directMatch) return directMatch[0];

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;

    return formatDateOnly(parsed);
  };

  const [sortState, setSortState] = useState<{
    [key: string]: { sortBy: string; sortOrder: "asc" | "desc" } | null;
  }>({
    docs: null,
    payouts: null,
    wip: null,
    predictionDoc: null,
    predictionPaid: null,
  });
  const [activePredictionsTab, setActivePredictionsTab] = useState<PredictionsTab>("docs");

  const handlePredictionsTabChange = (value: string) => {
    if (value === "docs" || value === "paid") {
      setActivePredictionsTab(value);
    }
  };

  const isClickable = variant === "hob" || variant === "osm" || variant === "team";

  const docColumns = useMemo(() => {
    if (variant === "director") return directorDocColumns;
    if (variant === "hob") return hobDocColumns;
    if (variant === "osm") return osmDocColumns;
    return teamDocColumns;
  }, [variant]);

  const paidColumns = useMemo(() => {
    if (variant === "director") return directorPaidColumns;
    if (variant === "hob") return hobPaidColumns;
    if (variant === "osm") return osmPaidColumns;
    return teamPaidColumns;
  }, [variant]);

  const wipColumns = useMemo(() => {
    if (variant === "director") return directorWIPColumns;
    if (variant === "hob") return hobWIPColumns;
    if (variant === "osm") return osmWIPColumns;
    return teamWIPColumns;
  }, [variant]);

  const wipHeaderColumnsMemo = useMemo(() => {
    if (variant === "director") return wipHeaderColumns;
    if (variant === "hob") return hobWIPHeaderColumns;
    if (variant === "osm") return osmWIPHeaderColumns;
    return teamWIPHeaderColumns;
  }, [variant]);

  const handleSort = (section: string, sortKey: string) => {
    setSortState((prev) => {
      const currentSort = prev[section];

      if (!currentSort || currentSort.sortBy !== sortKey) {
        return {
          ...prev,
          [section]: { sortBy: sortKey, sortOrder: "asc" },
        };
      } else if (currentSort.sortOrder === "asc") {
        return {
          ...prev,
          [section]: { sortBy: sortKey, sortOrder: "desc" },
        };
      } else {
        return {
          ...prev,
          [section]: null,
        };
      }
    });
  };
  
  const isDocsOutPredictionStatus = (status: string | undefined): boolean => {
    if (!status) return false;
    return status.trim().toLowerCase() === "docs out";
  };

  const normalizeKey = (value: string | undefined): string => value?.trim().toLowerCase() ?? "";

  const isPredictionForToday = (predictedDate: string | undefined): boolean => {
    if (!predictedDate) return false;

    const todayOnly = formatDateOnly(new Date());
    const predictionDateOnly = getDateOnlyFromApiDate(predictedDate);
    return predictionDateOnly === todayOnly;
  };

  const getPredictionNameForVariant = (prediction: { agentName: string; departmentName: string }): string | undefined => {
    if (variant === "team") return prediction.agentName;
    if (variant === "osm") return prediction.departmentName;
    return undefined;
  };

  const predictionsDocData = useMemo(() => {
    const predictions = data.predictionsDoc ?? [];
    return predictions.filter(
      (prediction) =>
        !isDocsOutPredictionStatus(prediction.applicationStatusName) &&
        isPredictionForToday(prediction.predictedDate)
    );
  }, [data.predictionsDoc]);

  const predictionsDocTableData = useMemo(() => {
    const predictions = data.predictionsDoc ?? [];
    if (variant !== "team" && variant !== "osm") {
      return predictions;
    }
    return predictions.filter((prediction) => !isDocsOutPredictionStatus(prediction.applicationStatusName));
  }, [data.predictionsDoc, variant]);

  const docsData = useMemo(() => {
    if ((variant !== "team" && variant !== "osm") || !Array.isArray(data.predictionsDoc)) {
      return data.docs;
    }

    const predictedCountByName = new Map<string, number>();

    for (const prediction of predictionsDocData) {
      const predictionName = getPredictionNameForVariant(prediction);
      if (!predictionName) {
        continue;
      }

      const key = normalizeKey(predictionName);
      predictedCountByName.set(key, (predictedCountByName.get(key) ?? 0) + 1);
    }

    return data.docs.map((doc) => {
      const key = normalizeKey(doc.name);
      return {
        ...doc,
        predicted: predictedCountByName.get(key) ?? 0,
      };
    });
  }, [data.docs, data.predictionsDoc, predictionsDocData, variant]);

  const sortedDocs = useMemo(() => {
    let sorted = [...docsData];
    const docsSort = sortState["docs"];
    if (docsSort) {
      sorted = sortPipelineData(sorted, docsSort.sortBy, docsSort.sortOrder);
    }
    return sorted;
  }, [docsData, sortState]);

  const wipWithTotals = useMemo((): DashboardWIPWithTotals[] => {
    return data.wiPs.map((w) => ({
      ...w,
      beforeOpeningTotal: w.docsOutBeforeOpening + w.readyToGoBeforeOpening + w.payoutBeforeOpening,
      liveTotal: w.docsOutLive + w.readyToGoLive + w.payoutLive,
    }));
  }, [data.wiPs]);

  const wipGapById = useMemo(() => {
    const map = new Map<number, number>();
    wipWithTotals.forEach((w) => map.set(w.id, w.beforeOpeningTotal));
    return map;
  }, [wipWithTotals]);

  const wipGapByName = useMemo(() => {
    const map = new Map<string, number>();
    wipWithTotals.forEach((w) => map.set(w.name, w.beforeOpeningTotal));
    return map;
  }, [wipWithTotals]);

  const enrichedPayouts = useMemo((): EnrichedPaidModel[] => {
    return data.payouts.map((p) => {
      const gap = wipGapById.has(p.id) ? wipGapById.get(p.id)! : (wipGapByName.get(p.name) ?? null);
      const pctFromOriginalGap = gap != null && gap !== 0 ? (p.paidFromStartOfTheDay / gap) * 100 : null;
      const pctPaidOverall = gap != null && gap !== 0 ? (p.actual / gap) * 100 : null;
      return { ...p, gap, pctFromOriginalGap, pctPaidOverall };
    });
  }, [data.payouts, wipGapById, wipGapByName]);

  const sortedPaid = useMemo(() => {
    let sorted = [...enrichedPayouts];
    const paidSort = sortState["payouts"];
    if (paidSort) {
      sorted = sortPipelineData(sorted, paidSort.sortBy, paidSort.sortOrder);
    }
    return sorted;
  }, [enrichedPayouts, sortState]);

  const hasDocRedocsColumn = useMemo(
    () => docColumns.some((c) => c.key === "totalReDoc"),
    [docColumns]
  );

  const docsTotals = useMemo(() => {
    let predictedSum = 0;
    let predictedActualSum = 0;
    let actualSum = 0;
    let totalReDocSum = 0;
    for (const d of docsData) {
      predictedSum += d.predicted;
      predictedActualSum += d.predictedActual;
      actualSum += d.actual;
      totalReDocSum += d.totalReDoc;
    }
    return { predictedSum, predictedActualSum, actualSum, totalReDocSum };
  }, [docsData]);

  const paidTotals = useMemo(() => {
    let gapColumnSum = 0;
    let gapSumForPct = 0;
    let paidStartSum = 0;
    let predictedSum = 0;
    let actualSum = 0;
    let actualSumForPctPaidOverall = 0;
    for (const p of enrichedPayouts) {
      gapColumnSum += p.gap ?? 0;
      predictedSum += p.predicted;
      actualSum += p.actual;
      if (p.gap != null) {
        gapSumForPct += p.gap;
        paidStartSum += p.paidFromStartOfTheDay;
        actualSumForPctPaidOverall += p.actual;
      }
    }
    const pctFromOriginalGap =
      gapSumForPct === 0 ? null : Math.round((paidStartSum / gapSumForPct) * 100);
    const pctPaidOverall =
      gapSumForPct === 0 ? null : Math.round((actualSumForPctPaidOverall / gapSumForPct) * 100);
    return {
      gapColumnSum,
      predictedSum,
      actualSum,
      pctFromOriginalGap,
      pctPaidOverall,
    };
  }, [enrichedPayouts]);

  const sortedWip = useMemo(() => {
    let sorted = [...wipWithTotals];
    const wipSort = sortState["wip"];
    if (wipSort) {
      sorted = sortPipelineData(sorted, wipSort.sortBy, wipSort.sortOrder);
    }
    return sorted;
  }, [wipWithTotals, sortState]);

  const sortedPredictionsDoc = useMemo(() => {
    let sorted = [...predictionsDocTableData];
    const s = sortState["predictionsDoc"];
    if (s) sorted = sortPipelineData(sorted, s.sortBy, s.sortOrder);
    return sorted;
  }, [predictionsDocTableData, sortState]);

  const sortedPredictionsPay = useMemo(() => {
    let sorted = [...(data.predictionsPaid ?? [])];
    const s = sortState["predictionsPaid"];
    if (s) sorted = sortPipelineData(sorted, s.sortBy, s.sortOrder);
    return sorted;
  }, [data.predictionsPaid, sortState]);

  const showPredictions = variant === "osm" || variant === "team";

  return (
    <>
      <div className="flex flex-col gap-3 md:flex-row">
        <div className="w-full md:basis-1/2">
          <StaticPipelineCard
            title="Docs"
            columns={docColumns}
            sortBy={sortState["docs"]?.sortBy}
            sortOrder={sortState["docs"]?.sortOrder}
            onSort={(sortKey) => handleSort("docs", sortKey)}
          >
            {sortedDocs.map((item, index) => (
              <DashboardRow
                key={`doc-${index}`}
                name={item.name}
                columns={docColumns}
                sortBy={sortState["docs"]?.sortBy}
                highlightColor="#FBFAFF"
                isClickable={isClickable}
                onNameClick={onNameClick}
              >
                <span>{item.predicted}</span>
                <ComparisonBadge actual={item.predictedActual} predicted={item.predicted} />
                <ComparisonBadge actual={item.actual} predicted={item.predicted} />
                {hasDocRedocsColumn && <span>{item.totalReDoc}</span>}
              </DashboardRow>
            ))}
            <DashboardRow
              key="doc-total"
              name="Total"
              columns={docColumns}
              highlightColor="#FBFAFF"
              isClickable={false}
              className="border-t font-semibold"
            >
              <span>{docsTotals.predictedSum}</span>
              <ComparisonBadge actual={docsTotals.predictedActualSum} predicted={docsTotals.predictedSum} />
              <ComparisonBadge actual={docsTotals.actualSum} predicted={docsTotals.predictedSum} />
              {hasDocRedocsColumn && <span>{docsTotals.totalReDocSum}</span>}
            </DashboardRow>
          </StaticPipelineCard>
        </div>
        <div className="w-full md:basis-1/2">
          <StaticPipelineCard
            title="Paid"
            columns={paidColumns}
            sortBy={sortState["payouts"]?.sortBy}
            sortOrder={sortState["payouts"]?.sortOrder}
            onSort={(sortKey) => handleSort("payouts", sortKey)}
            useFixedLayout={false}
          >
            {sortedPaid.map((item, index) => (
              <DashboardRow
                key={`paid-${index}`}
                name={item.name}
                columns={paidColumns}
                sortBy={sortState["payouts"]?.sortBy}
                highlightColor="#FBFAFF"
                isClickable={isClickable}
                onNameClick={onNameClick}
              >
                <span>{item.gap ?? "-"}</span>
                <span>{item.predicted}</span>
                <span>{item.actual}</span>
                <span>{item.pctFromOriginalGap != null ? `${Math.round(item.pctFromOriginalGap)}%` : "-"}</span>
                <span>{item.pctPaidOverall != null ? `${Math.round(item.pctPaidOverall)}%` : "-"}</span>
              </DashboardRow>
            ))}
            <DashboardRow
              key="paid-total"
              name="Total"
              columns={paidColumns}
              highlightColor="#FBFAFF"
              isClickable={false}
              className="border-t font-semibold"
            >
              <span>{paidTotals.gapColumnSum}</span>
              <span>{paidTotals.predictedSum}</span>
              <span>{paidTotals.actualSum}</span>
              <span>{paidTotals.pctFromOriginalGap != null ? `${paidTotals.pctFromOriginalGap}%` : "-"}</span>
              <span>{paidTotals.pctPaidOverall != null ? `${paidTotals.pctPaidOverall}%` : "-"}</span>
            </DashboardRow>
          </StaticPipelineCard>
        </div>
      </div>
      <StaticPipelineCard
        title="WIP"
        columns={wipColumns}
        headerColumns={wipHeaderColumnsMemo}
        sortBy={sortState["wip"]?.sortBy}
        sortOrder={sortState["wip"]?.sortOrder}
        onSort={(sortKey) => handleSort("wip", sortKey)}
      >
        {sortedWip.map((item, index) => (
          <DashboardRow
            key={`wip-${index}`}
            name={item.name}
            columns={wipColumns}
            sortBy={sortState["wip"]?.sortBy}
            highlightColor="#FBFAFF"
            isClickable={isClickable}
            onNameClick={onNameClick}
          >
            <span>{item.docsOutBeforeOpening}</span>
            <span>{item.readyToGoBeforeOpening}</span>
            <span>{item.payoutBeforeOpening}</span>
            <span>{item.beforeOpeningTotal}</span>
            <span>{item.docsOutLive}</span>
            <span>{item.readyToGoLive}</span>
            <span>{item.payoutLive}</span>
            <span>{item.liveTotal}</span>
          </DashboardRow>
        ))}
      </StaticPipelineCard>
      {showPredictions && (
        <>
          <p className="mt-4 text-lg font-semibold">
            Predictions
          </p>
          <Tabs value={activePredictionsTab} onValueChange={handlePredictionsTabChange} className="gap-3">
            <TabsList>
              <TabsTrigger value="docs">Docs</TabsTrigger>
              <TabsTrigger value="paid">Paid</TabsTrigger>
            </TabsList>
            <TabsContent value="docs">
              <StaticPipelineCard 
                title="Predicted to Doc" 
                columns={predictedDocColumns}
                sortBy={sortState["predictionsDoc"]?.sortBy}
                sortOrder={sortState["predictionsDoc"]?.sortOrder}
                onSort={(sortKey) => handleSort("predictionsDoc", sortKey)}
                stickyHeader={true}
              >
                {sortedPredictionsDoc.map((item, index) => (
                  <DashboardRow
                    key={`pred-doc-${item.applicationId}-${index}`}
                    name={item.customerName}
                    columns={predictedDocColumns}
                    sortBy={sortState["predictionsDoc"]?.sortBy}
                    highlightColor="#FBFAFF"
                  >
                    <span>{item.applicationStatusName}</span>
                    <span>{item.applicationId}</span>
                    <span>{item.agentName}</span>
                    <span>{item.departmentName}</span>
                    <span>{item.predictedReason ? item.predictedReason : "-"}</span>
                    <span>{formatPredictedDate(item.predictedDate)}</span>
                    <span>{item.daysSincePrediction} days</span>
                  </DashboardRow>
                ))}
              </StaticPipelineCard>
            </TabsContent>
            <TabsContent value="paid">
              <StaticPipelineCard 
                title="Predicted to Pay" 
                columns={predictedPayColumns}
                sortBy={sortState["predictionsPaid"]?.sortBy}
                sortOrder={sortState["predictionsPaid"]?.sortOrder}
                onSort={(sortKey) => handleSort("predictionsPaid", sortKey)}
                stickyHeader={true}
              >
                {sortedPredictionsPay.map((item, index) => (
                  <DashboardRow
                    key={`pred-paid-${item.applicationId}-${index}`}
                    name={item.customerName}
                    columns={predictedPayColumns}
                    sortBy={sortState["predictionsPaid"]?.sortBy}
                    highlightColor="#FBFAFF"
                  >
                    <span>{item.applicationId}</span>
                    <span>{item.agentName}</span>
                    <span>{item.departmentName}</span>
                    <span>{item.daysSincePrediction} days</span>
                  </DashboardRow>
                ))}
              </StaticPipelineCard>
            </TabsContent>
          </Tabs>
        </>
      )}
    </>
  );
};
